<script lang="ts" setup>
import { computed } from 'vue';
import type { ProgressBarType, Section } from '../types/formTypeStructure';
interface Props {
  currentStep: number;
  totalSteps: number;
  progressBarType?: ProgressBarType;
  sections?: Section[];
  currentSectionIndex?: number;
}
const props = defineProps<Props>();
// Progression en pourcentage
const progress = computed(() =>
  props.totalSteps > 0 ? (props.currentStep / props.totalSteps) * 100 : 0
);
// Type de barre
const type = computed(() => props.progressBarType?.type ?? 'default_step');
const shouldRender = computed(() => props.progressBarType?.visible !== false);
// Couleurs configurables (avec valeurs par défaut)
const fgColor = computed(() => props.progressBarType?.colors?.foreground || '#04406E');
const bgColor = computed(() => props.progressBarType?.colors?.background || '#e5e7eb');
// Sections visibles (pour les bulles). Pour l'instant on considère toutes les sections comme visibles.
const visibleSectionsWithIndex = computed(() =>
  (props.sections || []).map((section, index) => ({ section, originalIndex: index }))
);
const visibleSections = computed(() => visibleSectionsWithIndex.value.map((i) => i.section));
const currentVisibleIndex = computed(() => {
  if (props.currentSectionIndex == null) return 0;
  const idx = visibleSectionsWithIndex.value.findIndex(
    ({ originalIndex }) => originalIndex === props.currentSectionIndex
  );
  return idx >= 0 ? idx : 0;
});
const isPastelBubble = computed(
  () => type.value === 'section_bubble_pastel'
);
// Classes de base pour les bulles (sans couleurs)
const bubbleClass = (index: number) => {
  const active = index === currentVisibleIndex.value;
  return {
    'border-2': !active,
    'border-4': active,
  };
};
// Styles dynamiques pour les bulles (couleurs via progressBarType.colors)
const bubbleStyle = (index: number) => {
  const completed = index < currentVisibleIndex.value;
  const active = index === currentVisibleIndex.value;
  if (isPastelBubble.value) {
    if (completed) {
      return {
        backgroundColor: fgColor.value,
        color: '#ffffff',
        borderColor: fgColor.value,
      };
    }
    if (active) {
      return {
        backgroundColor: '#ffffff',
        color: fgColor.value,
        borderColor: fgColor.value,
      };
    }
    return {
      backgroundColor: '#ffffff',
      color: '#9ca3af',
      borderColor: bgColor.value,
    };
  }
  // section_bubble classique
  if (completed) {
    return {
      backgroundColor: fgColor.value,
      color: '#ffffff',
      borderColor: fgColor.value,
    };
  }
  if (active) {
    return {
      backgroundColor: '#ffffff',
      color: fgColor.value,
      borderColor: fgColor.value,
    };
  }
  return {
    backgroundColor: '#ffffff',
    color: '#9ca3af',
    borderColor: bgColor.value,
  };
};
// Styles dynamiques pour les traits entre les bulles
const lineStyle = (index: number) => {
  const completed = index < currentVisibleIndex.value;
  return {
    borderColor: completed ? fgColor.value : bgColor.value,
  };
};
// Styles pour les segments du type "pastel"
const pastelSegmentStyle = (index: number) => {
  const stepNumber = index + 1;
  const completed = stepNumber < props.currentStep;
  const active = stepNumber === props.currentStep;
  let backgroundColor = bgColor.value;
  let boxShadow = '';
  if (completed) {
    backgroundColor = fgColor.value;
  }
  if (active) {
    backgroundColor = fgColor.value;
    boxShadow = '0 0 0 2px rgba(0,0,0,0.05)';
  }
  return {
    minWidth: '20px',
    backgroundColor,
    boxShadow,
  };
};
</script>
<template>
  <div v-if="shouldRender" class="mt-4 w-full max-w-4xl mb-4">
    <!-- default_step -->
    <div v-if="type === 'default_step'">
      <div class="h-2 rounded-full overflow-hidden" :style="{ backgroundColor: bgColor }">
        <div
          class="h-full transition-all duration-500"
          :style="{ width: `${progress}%`, backgroundColor: bgColor }"
        />
      </div>
      <div class="text-right text-xs text-gray-500 mt-1">
        Étape {{ currentStep }} / {{ totalSteps }}
      </div>
    </div>
    <!-- linear -->
    <div v-else-if="type === 'linear'">
      <div class="h-2 rounded-full overflow-hidden" :style="{ backgroundColor: bgColor }">
      <div
          class="h-full transition-all duration-500"
          :style="{ width: `${progress}%`, backgroundColor: fgColor }"
        />
      </div>
    </div>
    <!-- pastel (segments) -->
    <div v-else-if="type === 'pastel'">
      <div class="flex items-center gap-1.5">
        <div
          v-for="(step, index) in totalSteps"
          :key="step"
          class="flex-1 h-3 rounded-full transition-all duration-500"
          :style="pastelSegmentStyle(index)"
        />
      </div>
      <div class="text-right font-semibold text-xs mt-1" :style="{ color: fgColor }">
        Étape {{ currentStep }} / {{ totalSteps }}
      </div>
    </div>
    <!-- custom -->
    <div v-else-if="type === 'custom'">
      <div
        class="h-2 rounded-full overflow-hidden"
        :style="{ backgroundColor: bgColor }"
      >
        <div
          class="h-full transition-all duration-500"
          :style="{
            width: `${progress}%`,
            backgroundColor: fgColor
          }"
      />
    </div>
    <div class="text-right text-xs text-gray-500 mt-1">
      Étape {{ currentStep }} / {{ totalSteps }}
      </div>
    </div>
    <!-- section_bubble & section_bubble_pastel -->
    <div
      v-else-if="type === 'section_bubble' || type === 'section_bubble_pastel'"
      class="w-full"
    >
      <div class="flex items-start justify-between">
        <template
          v-for="(section, indexVis) in visibleSections"
          :key="section.sectionId || indexVis"
        >
          <div v-if="indexVis > 0" class="flex-1 h-0 self-center border-t-2 border-dashed" :style="lineStyle(indexVis - 1)" />
          <div class="flex flex-col items-center px-2">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 z-10 relative"
              :class="bubbleClass(indexVis)"
              :style="bubbleStyle(indexVis)"
            >
              {{ indexVis + 1 }}
            </div>
            <div
              class="text-center font-medium text-xs mt-2 whitespace-nowrap"
              :style="{ color: indexVis <= currentVisibleIndex.value ? fgColor.value : '#9ca3af' }"
            >
              {{ section.title }}
            </div>
          </div>
        </template>
      </div>
      <!-- <div
        class="text-center font-semibold text-xs mt-2"
        :style="{ color: fgColor }"
      >
        Section {{ currentVisibleIndex + 1 }} / {{ visibleSections.length }}
      </div> -->
    </div>
    <!-- fallback -->
    <div v-else>
      <div class="h-2 rounded-full overflow-hidden" :style="{ backgroundColor: bgColor }">
        <div
          class="h-full transition-all duration-500"
          :style="{ width: `${progress}%`, backgroundColor: fgColor }"
        />
      </div>
      <div class="text-right text-xs text-gray-500 mt-1">
        Étape {{ currentStep }} / {{ totalSteps }}
      </div>
    </div>
  </div>
</template>